import {
  Component,
  EventEmitter,
  HostBinding,
  InjectionToken,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { DcAlertInnerService } from 'src/app/ui-kit/dc-alert-inner/dc-alert-inner.service';
import { ProductCaptureData } from '../../../model/interfaces/product-capture.interface';
import { AnalyticsService } from '../../../services/analytics.service';
import { AnalyticsRequestModalService } from '../analytics-request-modal/analytics-request-modal.service';
import { AnalyticsReportsTypes } from '../analytics.conts';
import { ANALYTICS_SOURCE, AnalyticsData } from '../analytics.interface';

export const ANALYTICS_BASE_TOKEN =
  new InjectionToken<AnalyticsTablesBaseComponent>('AnalyticsBaseComponent');

@Component({
  template: '',
  styleUrls: ['./analytics-tables-base.component.scss'],
})
export class AnalyticsTablesBaseComponent {
  @Input()
  data: AnalyticsData[];

  @Input()
  source: ANALYTICS_SOURCE;

  @Input()
  tenor: string;

  @Input()
  productCaptureDetail: ProductCaptureData;

  @Input()
  fvcAsOfDefault: NgbDateStruct;

  @Input()
  productType: string | null;

  @Input()
  productId: number;

  @Input()
  @HostBinding('class.active')
  active: boolean;

  loading: boolean;

  showTables: boolean;

  hasData: boolean;

  showWarnings: boolean;

  fourHundred: boolean;

  analyticsManuallyRequested = false;

  integrationJsonMessage: string[];

  integrationJsonValidationMessages: string[];

  disableRequestButton: boolean;

  messages = {
    emptyMessage: 'No data found.',
    totalMessage: 'Total',
  };

  @Output()
  loadData = new EventEmitter<void>();

  constructor(
    protected analyticsService: AnalyticsService,
    protected requestModalService: AnalyticsRequestModalService,
    public dcAlertInnerService: DcAlertInnerService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.isDataAvailable()) {
      if (changes.active?.currentValue) {
        if (changes.active.firstChange) {
          if (!this.data?.length) {
            this.loading = true;
          }
        } else {
          this.renderTablesOrRequestOrErrorScreen();
          this.loading = false;
        }
      }

      if (changes.data) {
        this.data = changes.data.currentValue;
        this.renderTablesOrRequestOrErrorScreen();
        this.loading = false;
      }
    }
  }

  fetchAnalytics(): void {
    this.loadData.emit();
  }

  renderTablesOrRequestOrErrorScreen(): void {
    // No data. Request Analytics.
    if (this.data == null || this.data.length === 0) {
      this.showTables = false;
      this.hasData = false;
      this.showWarnings = false;
      return;
    }

    this.prepareData();

    // Data to show some / all tables
    if (this.isDataAvailable()) {
      this.showTables = true;
      this.hasData = true;
      this.showWarnings = false;
      return;
    }

    const isSourceDataAvailableForReport =
      this.analyticsService.isSourceDataAvailable(
        this.data,
        this.source,
        this.reportType
      );

    const hasWarnings = this.analyticsService.hasRequestWithErrors(
      this.data,
      this.source,
      this.reportType
    );

    // No errors: Request submitted, results pending
    if (isSourceDataAvailableForReport && !hasWarnings) {
      this.showTables = false;
      this.hasData = false;
      this.showWarnings = false;

      this.fourHundred = false;
      this.analyticsManuallyRequested = true;
      return;
    }

    // Errors: Unable to retrieve analytics + display error messages if available
    this.showTables = false;
    this.hasData = hasWarnings;
    this.showWarnings = hasWarnings;

    // TODO extend this to also show CloudRisk messages. If they are returned in the same format as FVC errors, we can do so
    // by using the same function as below just changing the source. Design-wise, it's unclear if we'd want to show them
    // merged into the same list of errors or differentiate them somehow...
    if (this.source === ANALYTICS_SOURCE.FVC) {
      this.integrationJsonMessage =
        this.analyticsService.retrieveIntegrationJsonMessage(
          this.data,
          this.reportType
        );
      this.integrationJsonValidationMessages =
        this.analyticsService.retrieveIntegrationJsonValidationMessages(
          this.data,
          this.reportType
        );
      return;
    }
  }

  requestAnalyticsTriggerModal(): void {
    this.disableRequestButton = true;

    this.requestModalService
      .open({
        productId: this.productId,
        fvcAsOfDefault: this.fvcAsOfDefault,
        productType: this.productType,
        reportType: this.reportType,
      })
      .then(
        ({ result, errorMessage, tracerMessage }) => {
          if (result === 'success') {
            this.dcAlertInnerService.showAlert({
              type: 'success',
              message: `Analytics successfully requested. Press 'Refresh' to load data. This may take a few moments.`,
            });

            this.loading = false;
            this.showTables = false;
            this.hasData = false;
            this.showWarnings = false;
            this.disableRequestButton = false;

            this.fourHundred = false;
            this.analyticsManuallyRequested = true;
          }
          if (result === 'error') {
            const defaultErrorMessage = 'Data could not be retrieved.';
            const errorIndicator =
              'Unexpected response from data convert service';
            const regex =
              /"Integrator with id\/name.*?executed with failures\."/;
            const extractedMessageMatch = tracerMessage?.match(regex);

            const message =
              tracerMessage?.includes(errorIndicator) && extractedMessageMatch
                ? extractedMessageMatch[0].slice(1, -1)
                : errorMessage || defaultErrorMessage;

            this.dcAlertInnerService.showAlert({
              type: 'danger',
              message: `Error: ${message}`,
            });

            this.loading = false;
            this.showTables = false;
            this.hasData = false;
            this.showWarnings = false;

            this.disableRequestButton = false;
            this.analyticsManuallyRequested = true;
            this.fourHundred = true;
          }
        },
        (error) => {
          console.log(error);
          this.dcAlertInnerService.showAlert({
            type: 'danger',
            message: `Error: Something went wrong, please try again.`,
          });
        }
      );
  }

  // These functions should be implemented in the child components
  isDataAvailable(): boolean {
    return false;
  }

  prepareData(): void {}

  get reportType(): AnalyticsReportsTypes {
    return null;
  }
}
